import React from "react";
import { DateTime } from "luxon";

import {
  useAuthentication,
  useRetrieveCalendar,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { CalendarScope, Filter } from "@planzone/dto";
import { usePlanzonePermissions } from "@components/permissions";
import {
  eventToCalendarEvent,
  getMonthDates,
  getWeeks,
  isInWeek,
  milestoneToCalendarEvent,
  padMonthWeeks,
  sortCalendarByWeek,
  sortEventsByLength,
  sortUnavailabilitiesByLength,
  unavailabilityToCalendarEvent,
  calendarHolidaysToCalendarEvent,
  sortTasksByLength,
  taskToCalendarEvent,
  fromDayMarker,
} from "./utils";
import {
  CalendarEvent,
  CalendarMilestone,
  CalendarUnavailability,
  CalendarsHolidays,
  CalendarTask,
} from "./types";

function useCalendarData(calendarFilter: Filter & { scope?: CalendarScope }) {
  const userTimezone = DateTime.local().zoneName;

  const [cursor, setCursor] = React.useState(() => {
    const today = DateTime.now().setZone(userTimezone);
    return DateTime.utc(today.year, today.month, today.day).toISO() ?? "";
  });

  const { planzone, isRequestSuccess: hasPlanzone } =
    useRetrieveCurrentPlanzone();
  const { account } = useAuthentication();
  const { isAdministrator } = usePlanzonePermissions();

  const date = DateTime.fromISO(cursor, { zone: "utc" }).toJSDate();
  const month = padMonthWeeks(getWeeks(getMonthDates(date)));

  const filter = React.useMemo(
    () => ({
      ...calendarFilter,
      startDate: fromDayMarker(month[0][0]).toISO() ?? "",
      endDate:
        fromDayMarker(
          month[month.length - 1][month[month.length - 1].length - 1],
        ).toISO() ?? "",
    }),
    [month, calendarFilter],
  );

  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    calendar,
    retrieveCalendar,
  } = useRetrieveCalendar(filter);

  const events = React.useMemo(
    () => sortEventsByLength(calendar.events),
    [calendar.events],
  );

  const tasks = React.useMemo(
    () => sortTasksByLength(calendar.tasks),
    [calendar.tasks],
  );

  const sortedUnavailabilities = React.useMemo(
    () =>
      sortUnavailabilitiesByLength(
        calendar.unavailabilities.filter(
          ({ resource }) =>
            isAdministrator || resource.uuid === account.resource.uuid,
        ),
      ),
    [calendar.unavailabilities, isAdministrator, account.resource.uuid],
  );

  const weeks = React.useMemo(() => {
    return month.reduce<
      {
        week: Date[];
        events: CalendarEvent[];
        milestones: CalendarMilestone[];
        unavailabilities: CalendarUnavailability[];
        calendarsHolidays: CalendarsHolidays[];
        tasks: CalendarTask[];
      }[]
    >((nextWeeks, week) => {
      const calendarElements = sortCalendarByWeek(
        week,
        events,
        calendar.milestones,
        sortedUnavailabilities,
        calendar.calendarsHolidays,
        tasks,
        userTimezone,
      );

      return [
        ...nextWeeks,
        {
          week,
          events: calendarElements.events.map((event) =>
            eventToCalendarEvent(event, week, userTimezone),
          ),
          unavailabilities: calendarElements.unavailabilities.map(
            (unavailability) =>
              unavailabilityToCalendarEvent(unavailability, week, userTimezone),
          ),
          milestones: calendarElements.milestones.map((milestone) =>
            milestoneToCalendarEvent(milestone, week, userTimezone),
          ),
          calendarsHolidays: calendarElements.calendarHolidays.map(
            (calendarHolidays) =>
              calendarHolidaysToCalendarEvent(calendarHolidays, week),
          ),
          tasks: calendarElements.tasks.map((task) =>
            taskToCalendarEvent(task, week, userTimezone),
          ),
        },
      ];
    }, []);
  }, [
    month,
    events,
    tasks,
    calendar.milestones,
    sortedUnavailabilities,
    calendar.calendarsHolidays,
  ]);

  const currentWeek = React.useMemo(
    () => weeks.find(({ week }) => isInWeek(week, date, 6)) || weeks[0],
    [weeks, date],
  );

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveCalendar({ planzone, filter });
  }, [
    filter.startDate,
    filter.endDate,
    planzone,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveCalendar,
  ]);

  return {
    cursor,
    setCursor,
    planzone,
    hasPlanzone,
    isRequestPending,
    events,
    tasks,
    calendar,
    weeks,
    currentWeek,
  };
}

export { useCalendarData };
